import React, { useState, useEffect, useRef } from "react";
import { useApp } from "context";
import { Custom } from "components";
import { Grid } from "@mui/material";
import { IAppointment, IQarePlan } from "interfaces";
import { DocumentType, PairValue } from "types";
import { Constants, Enums, Helpers } from "utils";

type Props = {
  sx?: any;
  appointment: IAppointment | null;
  services: PairValue[];
  serviceProviderMap: Map<number, PairValue[]>;
  diagnoses: PairValue[];
  opened: boolean;
  onClose: () => void;
};

export const QarePlan = (props: Props) => {
  const { t, theme, locale, service: providedService } = useApp();

  const [providers, setProviders] = useState<PairValue[]>([]);
  const [serviceId, setServiceId] = useState<number | "">("");
  const [providerId, setProviderId] = useState<number | "">("");
  const [patientPlan, setPatientPlan] = useState("");
  const [diagnosis, setDiagnosis] = useState<PairValue | null>(null);
  const [documents, setDocuments] = useState<DocumentType[]>([]);
  const [secured, setSecured] = useState(false);
  const [isSecuredDisabled, setIsSecuredDisabled] = useState(true);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [isDeleteDisabled, setIsDeleteDisabled] = useState(true);

  // TODO: to remove the big red warning related to controlled and uncontrolled autocomplete a dummy div with key prop is used. bad programming!
  const [incrementAutocomplete, setIncrementAutocomplete] = useState(0);

  const hiddenInput = useRef(null);

  const qarePlan: IQarePlan | null = props.appointment?.qarePlan ?? null;

  useEffect(() => {
    setProviders(
      props.serviceProviderMap.get(qarePlan?.serviceId ?? NaN) ?? []
    );
    setServiceId(qarePlan?.serviceId ?? "");
    setProviderId(qarePlan?.providerId ?? "");
    setDiagnosis(
      props.diagnoses.find(
        (d: PairValue) => d.key === qarePlan?.diagnosisCodeId
      ) ?? null
    );
    setPatientPlan(qarePlan?.content ?? "");
    setDocuments(
      qarePlan?.documentNames.map((dn) => {
        return { documentName: dn, documentData: null };
      }) ?? []
    );
    setIsSecuredDisabled(!!qarePlan?.documentNames.length);
    setSecured(qarePlan?.secured ?? false);
    setIsDeleteDisabled(!qarePlan);
    setIncrementAutocomplete(incrementAutocomplete + 1);
  }, [props.appointment]);

  useEffect(() => {
    setIsSubmitDisabled(
      !(serviceId && providerId && diagnosis?.key && patientPlan)
    );
  }, [serviceId, providerId, diagnosis, patientPlan]);

  useEffect(() => {
    const newProviders = props.serviceProviderMap.get(Number(serviceId)) ?? [];
    !newProviders.find((p) => p.key === providerId) && setProviderId("");

    // TODO: setTimeout is used to remove the weird orange warning
    const timeout = setTimeout(() => setProviders(newProviders), 0);
    return () => clearTimeout(timeout);
  }, [serviceId]);

  useEffect(() => {
    documents.length && setSecured(true);
    setIsSecuredDisabled(!!documents.length);
  }, [documents]);

  const handleSelectService = (value: number) => setServiceId(value);

  const handleSelectProvider = (value: number) => setProviderId(value);

  const handleSelectDiagnosis = (
    event: React.SyntheticEvent<Element, Event>,
    value: PairValue | null
  ) => setDiagnosis(value);

  const handlePatientPlanChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => setPatientPlan(event.target.value);

  const handleCheckSecured = (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => setSecured(checked);

  const handleQarePlanSubmit = async () => {
    const [status, response] = await providedService.qarePlan.upsert(
      props.appointment?.appointmentId as number,
      serviceId as number,
      providerId as number,
      diagnosis?.key,
      patientPlan,
      secured,
      documents
    );

    if (status === Enums.EnumResponse.Success) props.onClose();
  };

  const handleQarePlanDelete = async () => {
    const [status, response] = await providedService.qarePlan.delete(
      props.appointment?.appointmentId as number
    );

    if (status === Enums.EnumResponse.Success) props.onClose();
  };

  // TODO: what is happening here exactly?
  const handleDocumentsSelect = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = e.target.files;

    if (files && files.length > 0) {
      const fileReadPromises = Array.from(files).map((file) => {
        return new Promise<ArrayBuffer | null>((resolve) => {
          const fileReader = new FileReader();

          fileReader.onload = (event: ProgressEvent<FileReader>) => {
            const fileContentBuffer = event.target?.result as ArrayBuffer;

            setDocuments((prevDocuments) => [
              ...prevDocuments,
              {
                documentName: file.name,
                documentData: fileContentBuffer,
              },
            ]);

            resolve(fileContentBuffer);
          };

          fileReader.onloadend = () => {
            if (fileReadPromises.every((p) => p !== undefined)) {
              (hiddenInput.current as any).value = "";
            }
          };

          fileReader.readAsArrayBuffer(file);
        });
      });

      await Promise.all(fileReadPromises);
    }
  };

  return (
    <Custom.Modal
      opened={props.opened}
      header={t.title.myqare_plan}
      onClose={props.onClose}
    >
      <Grid container spacing={theme.margin.sm}>
        <Grid item xs={12} md={6}>
          <Custom.TextField
            label={t.label.name}
            readOnly
            value={Helpers.replaceComma(props.appointment?.username ?? "")}
          ></Custom.TextField>
        </Grid>
        <Grid item xs={12} md={6}>
          <Custom.TextField
            label={t.label.check_in_datetime}
            readOnly
            value={new Date(
              props.appointment?.checkInTime ?? ""
            ).toLocaleTimeString(locale, Constants.DATE_FORMAT)}
          ></Custom.TextField>
        </Grid>
        <Grid item xs={12} md={12} sx={{ marginTop: "3rem" }}>
          <div key={incrementAutocomplete}>
            <Custom.Autocomplete
              label={t.label.diagnosis}
              items={props.diagnoses}
              onChange={handleSelectDiagnosis}
              multiple={false}
              value={diagnosis}
            />
          </div>
        </Grid>
        <Grid item xs={12} md={12}>
          <Custom.TextField
            multiline={true}
            label={t.label.patient_plan}
            value={patientPlan}
            onChange={handlePatientPlanChange}
          ></Custom.TextField>
        </Grid>
        <Grid item xs={12} md={6}>
          <Custom.Select
            label={t.label.next_service}
            items={props.services}
            onChange={handleSelectService}
            value={serviceId}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Custom.Select
            label={t.label.next_provider}
            items={providers}
            onChange={handleSelectProvider}
            value={providerId}
          />
        </Grid>
        <Grid item xs={12} md={10}>
          <input
            type="file"
            multiple
            style={{ display: "none" }}
            onChange={handleDocumentsSelect}
            ref={hiddenInput}
          />
          <Custom.Button
            sx={{ marginBottom: "0.5rem" }}
            onClick={() => (hiddenInput.current as any)?.click()}
            variant="outlined"
          >
            {t.action.attach}
          </Custom.Button>
          <div></div>
          {documents.map((d) => {
            return (
              <Custom.Chip
                label={d.documentName}
                key={d.documentName}
                sx={{ marginRight: "0.5rem", marginBottom: "0.5rem" }}
                onDelete={() =>
                  setDocuments(
                    documents.filter((_) => _.documentName !== d.documentName)
                  )
                }
              />
            );
          })}
        </Grid>
        <Grid item xs={12} md={2}>
          <Custom.Checkbox
            label={t.label.secured}
            onChange={handleCheckSecured}
            checked={secured}
            disabled={isSecuredDisabled}
          />
        </Grid>
      </Grid>
      <div style={{ marginTop: "0.75rem" }}>
        <Custom.Button
          size="large"
          disabled={isSubmitDisabled}
          onClick={handleQarePlanSubmit}
        >
          {t.action.submit}
        </Custom.Button>
        <Custom.Button
          sx={{ float: "right" }}
          size="large"
          disabled={isDeleteDisabled}
          onClick={handleQarePlanDelete}
        >
          {t.action.delete}
        </Custom.Button>
      </div>
    </Custom.Modal>
  );
};
